import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CloseIcon, EditIcon, Modal, PlusIcon, Select, useToast } from "@signet/ui";
import { CreatorAvatar, type CreatorPhotos } from "@signet/web-kit";
import { openSlots } from "../../shared/phones.ts";
import { fetchStockCreators, savePhonePlan, stockKeys, type Stock } from "./api.ts";

/* ──────────────────────────────────────────────────────────────────────────
   A phone's plan (Ben, 2026-10-28): which creator each of its next accounts
   is for. Ben planned phones in his own notes so the poster knew which
   account goes where; now a manager presses Assign under the phone and
   picks a creator per slot.

   A slot fills itself when a live account for that creator is on the phone
   (shared/phones.ts openSlots), so nothing has to be ticked off. A banned
   account leaves its slot open again, which is what the poster needs to
   know: that phone wants a new account for her.
   ────────────────────────────────────────────────────────────────────────── */

type PlanPhone = { poster: string; label: string; plan: string[]; live: Stock[] };

/** The open slots, one quiet line each, read only for everyone. */
export function PlannedSlots({ g, photos, size = 28 }: { g: PlanPhone; photos: CreatorPhotos; size?: number }) {
  const open = openSlots(g.plan, g.live);
  if (!open.length) return null;
  return (
    <ul className="igs-plan-slots" aria-label={`Planned for phone ${g.label}`}>
      {open.map((name, i) => (
        <li key={`${name}-${i}`} className="igs-plan-slot">
          <CreatorAvatar name={name} src={photos(name)} size={size} />
          <span>
            Planned: <b>{name}</b>
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Assign, under the phone, for those who manage the test. */
export function PlanButton({ g, photos }: { g: PlanPhone; photos: CreatorPhotos }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="igs-plan-foot">
      <button type="button" className="text-button igs-phone-note-button" onClick={() => setOpen(true)} aria-label={`Assign creators to phone ${g.label}`}>
        <EditIcon size={13} /> Assign
      </button>
      {open ? <PlanModal g={g} photos={photos} onClose={() => setOpen(false)} /> : null}
    </div>
  );
}

function PlanModal({ g, photos, onClose }: { g: PlanPhone; photos: CreatorPhotos; onClose: () => void }) {
  const client = useQueryClient();
  const toast = useToast();
  const roster = useQuery({ queryKey: [...stockKeys.all, "creators"], queryFn: fetchStockCreators, staleTime: 60_000 });
  // An empty plan starts with one slot to fill, so the dialog never opens on nothing.
  const [slots, setSlots] = useState<(string | null)[]>(g.plan.length ? g.plan : [null]);
  const options = [...new Set((roster.data?.creators ?? []).map((name) => name.trim().toUpperCase()))].sort().map((name) => ({ value: name, label: name, image: photos(name) }));
  const chosen = slots.filter((name): name is string => Boolean(name));
  // Which slots a live account already fills, worked out the same way the phone shows them.
  const open = openSlots(chosen, g.live);
  const filledAt = (() => {
    const left = [...open];
    return chosen.map((name) => {
      const i = left.indexOf(name);
      if (i === -1) return true;
      left.splice(i, 1);
      return false;
    });
  })();
  const save = useMutation({
    mutationFn: () => savePhonePlan({ poster: g.poster, phone: g.label, creators: chosen }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: stockKeys.all });
      toast.success(`Phone ${g.label} planned`, chosen.length ? `${chosen.length} ${chosen.length === 1 ? "slot" : "slots"}` : "Plan cleared");
      onClose();
    },
    onError: (e) => toast.error("Couldn't save the plan", e instanceof Error ? e.message : undefined),
  });
  let n = 0;
  return (
    <Modal
      open
      onClose={onClose}
      title={`Assign phone ${g.label}`}
      description="Pick the creator for each account this phone should hold. A slot fills itself once an account for that creator is on the phone."
      footer={
        <>
          <button type="button" className="secondary-button" onClick={onClose}>Cancel</button>
          <button type="button" className="button brass" disabled={save.isPending || (!chosen.length && !g.plan.length)} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <ol className="igs-plan-edit">
        {slots.map((name, i) => {
          const filled = name ? filledAt[n++] : false;
          return (
            <li key={i}>
              <span className="igs-plan-num">{i + 1}</span>
              <Select
                label={`Slot ${i + 1}`}
                value={name}
                options={options}
                onChange={(next) => setSlots((all) => all.map((s, j) => (j === i ? next : s)))}
                placeholder={roster.isLoading ? "Loading creators…" : "Choose a creator"}
                searchable
              />
              {filled ? <span className="igs-plan-filled">On the phone</span> : null}
              <button type="button" className="icon-button ghost small" onClick={() => setSlots((all) => all.filter((_, j) => j !== i))} aria-label={`Remove slot ${i + 1}`} title="Remove">
                <CloseIcon size={14} />
              </button>
            </li>
          );
        })}
      </ol>
      <button type="button" className="text-button igs-plan-add" disabled={slots.length >= 20} onClick={() => setSlots((all) => [...all, null])}>
        <PlusIcon size={13} /> Add a slot
      </button>
    </Modal>
  );
}
